import React, { useEffect, useState } from 'react';
import { get, has } from 'lodash';

import { apiClient } from '../../../helpers/apiHelperV2';
import Spinner from '../../components/Spinner/Spinner';

import { trackCheckoutStart, trackDataLayer } from '../../components/Tracking/Matomo';
import { toast } from '../../../helpers/toastHelper';
import { createPayPalOrder } from '../../../helpers/CheckoutHelper';
import { trackPinterestEvent } from '../../components/Tracking/Pinterest';
import { PayPalButtons } from '@paypal/react-paypal-js';

function getDeliveryAddress(paypalAuthorize) {
    const lastName = get(paypalAuthorize, 'purchase_units[0].shipping.name.full_name', '').split(' ').slice(-1).pop();
    const country = has(paypalAuthorize, 'purchase_units[0].shipping.address.country_code')
        ? get(paypalAuthorize, 'purchase_units[0].shipping.address.country_code', '')
        : get(paypalAuthorize, 'payer.name.address.country_code', '');

    if (country !== 'DE') {
        throw new Error('Bestellungen außerhalb Deutschlands werden nicht unterstützt.');
    }

    return {
        city: has(paypalAuthorize, 'purchase_units[0].shipping.address.admin_area_2')
            ? get(paypalAuthorize, 'purchase_units[0].shipping.address.admin_area_2', '')
            : get(paypalAuthorize, 'payer.name.address.admin_area_2', ''),
        zipCode: has(paypalAuthorize, 'purchase_units[0].shipping.address.postal_code')
            ? get(paypalAuthorize, 'purchase_units[0].shipping.address.postal_code', '')
            : get(paypalAuthorize, 'payer.name.address.postal_code', ''),
        street: has(paypalAuthorize, 'purchase_units[0].shipping.address.address_line_1')
            ? get(paypalAuthorize, 'purchase_units[0].shipping.address.address_line_1', '')
            : get(paypalAuthorize, 'payer.name.address.address_line_1', ''),
        additionalAddressInfo: has(paypalAuthorize, 'purchase_units[0].shipping.address.address_line_2')
            ? get(paypalAuthorize, 'purchase_units[0].shipping.address.address_line_2', '')
            : get(paypalAuthorize, 'payer.name.address.address_line_2', ''),
        country: 'Deutschland',
        lastName: lastName,
        firstName: get(paypalAuthorize, 'purchase_units[0].shipping.name.full_name', '').split(lastName).join('').trim(),
    };
}

function getInvoiceAddress(paypalAuthorize) {
    const country = has(paypalAuthorize, 'payer.name.address.country_code')
        ? get(paypalAuthorize, 'payer.name.address.country_code', '')
        : get(paypalAuthorize, 'purchase_units[0].shipping.address.country_code', '');

    if (country !== 'DE') {
        throw new Error('Bestellungen außerhalb Deutschlands werden nicht unterstützt.');
    }

    return {
        city: has(paypalAuthorize, 'payer.name.address.admin_area_2')
            ? get(paypalAuthorize, 'payer.name.address.admin_area_2', '')
            : get(paypalAuthorize, 'purchase_units[0].shipping.address.admin_area_2', ''),
        zipCode: has(paypalAuthorize, 'payer.name.address.postal_code')
            ? get(paypalAuthorize, 'payer.name.address.postal_code', '')
            : get(paypalAuthorize, 'purchase_units[0].shipping.address.postal_code', ''),
        street: has(paypalAuthorize, 'payer.name.address.address_line_1')
            ? get(paypalAuthorize, 'payer.name.address.address_line_1', '')
            : get(paypalAuthorize, 'purchase_units[0].shipping.address.address_line_1', ''),
        additionalAddressInfo: has(paypalAuthorize, 'payer.name.address.address_line_2')
            ? get(paypalAuthorize, 'payer.name.address.address_line_2', '')
            : get(paypalAuthorize, 'purchase_units[0].shipping.address.address_line_2', ''),
        country: 'Deutschland',
        lastName: get(paypalAuthorize, 'payer.name.surname', ''),
        firstName: get(paypalAuthorize, 'payer.name.given_name', ''),
        payerId: get(paypalAuthorize, 'payer.payer_id', ''),
    };
}

function PayPalCheckout({
    markenStandort = {},
    standortUrl,
    selectedSublocation,
    type = 'online-kauf',
    cart = {},
    products = [],
    history,
    existingUser,
    validateCartBeforeCheckout,
}) {
    const [loading, setLoading] = useState(false);

    async function onClick(data, actions) {
        if (!validateCartBeforeCheckout) {
            return;
        }

        const isValid = await validateCartBeforeCheckout();
        if (!isValid) {
            window.UIkit.notification('Ein Artikel im Warenkorb ist nicht mehr verfügbar. Bitte überprüfen Sie Ihren Warenkorb.', {
                status: 'warning',
                pos: 'top-right',
            });
            return actions.reject();
        }
    }

    async function createOrder(data, actions) {
        trackCheckoutStart({ legalType: 'online-kauf' });
        trackDataLayer({
            eventName:"begin_checkout", 
            products: cart.products, 
            standort: markenStandort, 
            value: cart.grossTotal,
            isOnline: true
        })
        trackPinterestEvent('checkout', {
            legalType: 'online-kauf',
        });

        return actions.order.create(createPayPalOrder({ cart }));
    }

    async function approveOrder(data, actions) {
        try {
            setLoading(true);

            const paymentDetails = await actions.order.get();

            const deliveryAddress = getDeliveryAddress(paymentDetails);
            const invoiceAddress = getInvoiceAddress(paymentDetails);

            const { data: user } = await apiClient.post('/api/v1/checkout/paypal/user', {
                existingUser,
                userData: paymentDetails,
                standortUrl,
            });
            let checkoutData = {
                payment: {
                    paymentDetails: paymentDetails,
                    paymentProvider: 'PayPal',
                    completed: true,
                },
                cart: {
                    ...cart,
                    products: cart.products.map(p => {
                        return {
                            ...p,
                            selectedLieferweg: {
                                ...(p.selectedLieferweg || {}),
                                type: 'paypalVersand',
                                paymentType: 'paypalVersand',
                            },
                        };
                    }),
                },
                userdata: {
                    deliveryAddress,
                    invoiceAddress,
                    infoEmail: get(paymentDetails, 'payer.email_address', ''),
                    completed: true,
                },
                user: user.data._id,
            };

            await apiClient.post('/api/v1/checkout/InitializePaypalCheckout', {
                products,
                cart,
                standortUrl,
                selectedSublocation,
                type,
                sessionId: cart.sessionId,
                checkoutData,
            });

            const { data: nextUrl } = await apiClient.post('/api/v1/getNextCheckoutUrl/adresse', {
                sessionId: cart.sessionId,
            });

            if (nextUrl.data.redirectUrl) history.push(nextUrl.data.redirectUrl);
        } catch (err) {
            toast({ err, message: 'Fehler beim Abschicken der Bestellung, bitte wenden Sie sich an den Support.' });
        } finally {
            setLoading(false);
        }
    }

    async function onError(err) {
        toast({ err, message: 'Fehler beim Abschicken der Bestellung, bitte wenden Sie sich an den Support.' });
    }

    return (
        <div className="shop_wrapper uk-margin-small-top">
            {loading && <Spinner />}
            <PayPalButtons 
                onClick={onClick}
                createOrder={createOrder}
                onApprove={approveOrder}
                onError={onError}
                style={{
                    tagline: false,
                    label: 'pay',
                    height: 34,
                }} 
            />
        </div>
    );
}

export default PayPalCheckout;
